import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';

import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FormatNumPipe, TitleCasePipe, TrackedEvents } from '@innovation-os-ng/ui/utils';
import { ColorPalettes, ThemeService, VizToolsNgModule } from '@innovation-os-ng/ui/viz-tools-ng';

import { ZoomTransformation } from '../../../common/zoom/models/zoom-model';
import { CanvasCursor, CirclePacking } from '../../models/circlePackingModel';

import { CanvasContext } from '../../class/context-class';
import { setZoom } from '../../class/zoom-class';

import { bounds } from '../../render-utils/dataProcessing/helpers/bounds';
import { dataRange } from '../../render-utils/dataProcessing/helpers/ranges';
import { packing } from '../../render-utils/dataProcessing/packing';
import { getRelativesSelected } from '../../render-utils/ui-helpers/getRelativeSelection';
import { transition, zoomTransformation } from '../../render-utils/ui-helpers/zoomTransformation';

import { circleRenderRecursive } from '../../render-utils/recursions/circleRenderRecursive';
import { textRenderRecursive } from '../../render-utils/recursions/textRenderRecursive';

import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Ui2ButtonModule } from '@innovation-os-ng/ui/ui2-button';
import { TranslateModule } from '@itonics/ngx-translate-simple';
import { CommonChartModule } from '../../../common/common-chart.module';
import { RenderOptions } from '../../../common/zoom/models/render-options';
import { ScaleTextPipe } from '../../../pipes/scaleText';
import {
  BORDER_RADIUS_TOOLTIP,
  CANVAS_HEIGHT,
  DEFAULT_RENDER_OPTIONS,
  FONT_SIZE_TOOLTIP,
  FONT_SIZE_TOOLTIP_IDEAL,
  MAIN_DEPTH,
  PADDING_TOOLTIP,
  STROKE_CIRCLE_UI,
  STROKE_CIRCLE_UI_IDEAL,
  STROKE_TOOLTIP,
  STROKE_TOOLTIP_IDEAL,
  TITLE_OFFSET
} from '../../render-utils/constants';
import { findNode } from '../../render-utils/recursions/find-node';
import { findParentNode } from '../../render-utils/recursions/find-parent-node';
import { CirclePackingUiComponent } from '../circle-packing-ui/circle-packing-ui.component';

@Component({
  selector: 'chart-circle-packing-canvas',
  templateUrl: './circle-packing-canvas.component.html',
  styleUrls: ['./circle-packing-canvas.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    CommonChartModule,
    CirclePackingUiComponent,
    VizToolsNgModule,
    TranslateModule,
    Ui2ButtonModule,
    TitleCasePipe,
    ScaleTextPipe
  ]
})
export class CirclePackingCanvasComponent implements OnInit, AfterViewInit {
  private context: CanvasRenderingContext2D;
  public size: {
    width: number;
    height: number;
  } = {
    width: 800,
    height: CANVAS_HEIGHT
  };

  /**
   * ZOOM
   */

  private zoom;
  public zoomTransformation: ZoomTransformation;
  @ViewChild('svgContainer')
  public svgContainer: ElementRef;

  private svgElm: HTMLCanvasElement;
  public ZOOM_IN_JUMP = 0.8;
  public ZOOM_OUT_JUMP = 1.2;

  /**
   * COLORS
   */
  public colorCategoryMap: {
    [key: string]: string;
  };

  /*
    Use to store colors
  */
  @Input()
  categoryKey: string;

  /**
   * CANVAS
   */
  private canvas: CanvasContext;
  // In case we need multiple instances of canvas we need an ID
  // to get the canvas dom element and init the rendering
  public canvasId: string;

  /**
   * Nodes
   */
  public activeNode: CirclePacking;
  // in order to be able to go back to the previous ancestor
  // we need to store the parent
  public hoveredNode: CirclePacking;

  // Selected circles
  public selectedChildren: Array<CirclePacking>;

  // Level of zoom in depth
  public actualDepth = 1;

  /**
   *  STYLES
   */
  public STROKE_CIRCLE_UI = STROKE_CIRCLE_UI;
  public STROKE_CIRCLE_UI_IDEAL = STROKE_CIRCLE_UI_IDEAL;
  public STROKE_TOOLTIP = STROKE_TOOLTIP;
  public STROKE_TOOLTIP_IDEAL = STROKE_TOOLTIP_IDEAL;
  public PADDING_TOOLTIP = PADDING_TOOLTIP;
  public BORDER_RADIUS_TOOLTIP = BORDER_RADIUS_TOOLTIP;
  public FONT_SIZE_TOOLTIP = FONT_SIZE_TOOLTIP;
  public FONT_SIZE_TOOLTIP_IDEAL = FONT_SIZE_TOOLTIP_IDEAL;

  @Input()
  noData: boolean;

  //  Because we don't want to return the entire tree on selection
  // we can ask what property we want to return when we emit the selection
  // By default is the id
  @Input()
  modelProperty: string;

  /**
   * DATA
   */

  public circlesData: CirclePacking;
  public dataRange: Array<number>;
  @Input()
  set data(data: CirclePacking) {
    if (!data?.children?.length) {
      this.noData = true;

      // we clean render
      this.cleanData();
      this.cleanCanvas();

      return;
    }

    // We initialize the map first, so the colors are available
    // when we generate the circle packing structure
    this.initColorMap(data);
    this.circlesData = this.generateCircleData(data);

    this.navigationStart.next(null);
    this.noData = false;

    /*

      If there is some active node that has children we zoom in
      preventing refresh data with the same categories

      For that, we need to find the active node in the new data
      if the active node does not have children in the new data we,
      we jump one level up.
    */

    const prevActiveNode = findNode(this.circlesData, this.activeNode?.id);
    const prevActiveNodeParent = findNode(this.circlesData, prevActiveNode?.parent);
    const nextActiveNode = prevActiveNode?.children?.length ? prevActiveNode : prevActiveNodeParent;

    if (nextActiveNode) {
      this.zoomIn(nextActiveNode);
    } else {
      this.activeNode = null;
      this.actualDepth = MAIN_DEPTH;

      this.zoom.transform(transition(this.svgElm), zoomTransformation(bounds(this.circlesData.children), this.size));
    }
  }

  /**
   * ngMODEL
   */

  @Output()
  selectModelChange: EventEmitter<Array<string>> = new EventEmitter();

  @Output()
  onZoomIn: EventEmitter<CirclePacking> = new EventEmitter();

  @Output()
  onZoomOut: EventEmitter<string> = new EventEmitter();

  @Output()
  public remove: EventEmitter<CirclePacking> = new EventEmitter();

  // Track events via parent/ancestor
  @Output()
  public trackEvent: EventEmitter<string> = new EventEmitter();

  public actualSelectModel: Array<string> = [];

  @Input()
  get selectModel() {
    return this.actualSelectModel;
  }

  set selectModel(values: Array<string>) {
    if ((this.actualSelectModel || []).sort().join('-') === (values || []).sort().join('-')) {
      return;
    }

    this.actualSelectModel = values; // io management
    this.updateModel(values, undefined, true);
  }

  /**
   * MAPs
   */

  public selectedMap: {
    [key: string]: boolean;
  } = {};

  /*

    ZOOM CONTROLS
  */

  @Input()
  public enableZoomScroll: boolean;

  public zooming: boolean;

  // In order to use chart-zoom-controls
  // we need to collect some data
  public controlConfig: {
    rectZoom: ElementRef;
    shapeWrapper: ElementRef;
    svgContainer: ElementRef;
    zoom: any; // D3 zoom
  };

  // Because each child could render in different moments
  // we subscribe to them so we can initiate the zoom controls once we have everything ready
  private rectZoomSubject: Subject<ElementRef> = new Subject();
  private shapeWrapperSubject: Subject<ElementRef> = new Subject();
  private zoomSubject: Subject<any> = new Subject();
  private initiatedControls: Subject<void> = new Subject();

  /*
    Disable interactions
  */
  @Input()
  disableSelection: boolean;

  @Input()
  disableRemove: boolean;

  /*

     View children
  */

  @ViewChild('rectZoom')
  set rectZoom(rectZoom: ElementRef) {
    if (rectZoom) {
      this.rectZoomSubject.next(rectZoom);
    }
  }
  @ViewChild('shapeWrapper')
  set shapeWrapper(shapeWrapper: ElementRef) {
    if (shapeWrapper) {
      this.shapeWrapperSubject.next(shapeWrapper);
    }
  }

  /*

    We need to detect when the navigation ends,
    the navigation happens when the user:

    1. clicks in a circle with children
    2. clicks in back button
    3. data is been updating

    We can detect when the navigation ends combining two events:

    1. zoom in/out: will tell us when the navigation starts
    2. zoom ends: will tell us when the render finishes

  */

  private zoomEnd: Subject<void> = new Subject();
  private navigationStart: Subject<string> = new Subject();

  // Canvas cursor, we want the cursor changes when is been dragged
  public canvasCursor: string = CanvasCursor.GRAB;
  // we use this to determine when to show the grabbing cursor
  public prevScaleTransform: number;
  public canvasPadding: number = 20;

  @HostBinding('style.height')
  hostHeight = `${CANVAS_HEIGHT}px`;

  constructor(
    private readonly host: ElementRef,
    private readonly themeService: ThemeService,
    private readonly formatNumber: FormatNumPipe,
    private readonly titleCase: TitleCasePipe
  ) {
    combineLatest([this.rectZoomSubject, this.shapeWrapperSubject, this.zoomSubject])
      .pipe(takeUntil(this.initiatedControls))
      .subscribe(([rectZoom, shapeWrapper, zoom]) => {
        if (!rectZoom || !rectZoom.nativeElement || !shapeWrapper || !shapeWrapper.nativeElement || !zoom) {
          return;
        }

        // Once we have all the elements ready + the zoom object we configure zoom control

        setTimeout(() => {
          this.controlConfig = {
            rectZoom,
            shapeWrapper,
            zoom,
            svgContainer: this.svgContainer
          };
        }, 0);

        // And we unsubscribe once we have controls initiated
        this.initiatedControls.next(null);
      });

    /*

    We need to detect when the navigation ends,
    the navigation happens when the user:

    1. clicks in a circle with children
    2. clicks in back button
    3. data is been updating

    We can detect when the navigation ends combining two events:

    1. zoom in/out: will tell us when the navigation starts
    2. zoom ends: will tell us when the render finishes

  */
    this.navigationStart.pipe(takeUntilDestroyed()).subscribe(() => {
      this.zooming = true;
    });
    this.zoomEnd.pipe(takeUntilDestroyed()).subscribe(() => {
      this.zooming = false;
    });
  }

  ngOnInit() {
    // In case we need multiple instances of canvas we need an ID
    // to get the canvas dom element and init the rendering
    this.canvasId = this.generateUri(8);
  }

  async ngAfterViewInit() {
    await this.initCanvas();

    this.svgElm = this.svgContainer.nativeElement;

    this.setZoom();
  }

  // We want to re-render canvas on resize
  @HostListener('window:resize', ['$event'])
  onresize() {
    this.initCanvas();

    if (this.size && this.svgElm && this.zoom && this.circlesData && this.circlesData.children) {
      this.zoom.transform(transition(this.svgElm), zoomTransformation(bounds(this.circlesData.children), this.size));
    }
  }

  private initCanvas(): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(() => {
        this.size.width = this.host.nativeElement.offsetWidth;
        this.size.height = CANVAS_HEIGHT;

        this.canvas = this.canvas = new CanvasContext(this.size, this.canvasId);

        this.context = this.canvas.getContext();

        resolve();
      }, 0);
    });
  }

  private setZoom() {
    if (!this.zoom) {
      this.zoom = setZoom(this.svgElm);

      this.zoom
        .on('zoom', (event) => {
          this.zoomed(event.transform);

          if (event.transform) {
            //  Add grab and grabbing cursors on drag and drop
            if (this.prevScaleTransform === event?.transform?.k) {
              this.canvasCursor = CanvasCursor.GRABBING;
            } else {
              this.canvasCursor = CanvasCursor.GRAB;
            }
            this.prevScaleTransform = event?.transform?.k;
          }
        })
        .on('end', () => {
          this.canvasCursor = CanvasCursor.GRAB;
          /*
            We subscribe when zoom ends to be able to
            perform actions when is zooming
          */
          this.zoomEnd.next(null);
        });

      this.zoomSubject.next(this.zoom);

      setTimeout(() => {
        if (this.circlesData && this.circlesData.children) {
          this.zoom.transform(transition(this.svgElm), zoomTransformation(bounds(this.circlesData.children), this.size));
        }
      }, 500);
    }
  }

  private zoomed(transform) {
    this.zoomTransformation = transform;

    this.render(DEFAULT_RENDER_OPTIONS);
  }

  private render(renderOptions?: RenderOptions) {
    let renderCircles = true;
    let renderText = true;

    if (renderOptions) {
      renderCircles = renderOptions.hasOwnProperty('circles');
      renderText = renderOptions.hasOwnProperty('text');
    }

    this.canvas.startRender(this.zoomTransformation);

    if (renderCircles) {
      // RENDER RECURSIVELY CIRCLES

      const opts = {
        circlesPacking: this.selectedChildren,
        context: this.context,
        zoom: this.zoomTransformation,
        renderDepth: renderOptions?.circles?.renderDepth
      };

      circleRenderRecursive(opts.circlesPacking, opts.context, opts.zoom, opts.renderDepth);
    }

    if (renderText) {
      // RENDER RECURSIVELY TEXT

      const opts = {
        circlesPacking: this.selectedChildren,
        context: this.context,
        actualDepth: this.actualDepth,
        formatValue: this.formatNumber.transform,
        renderOptions
      };
      textRenderRecursive(opts.circlesPacking, opts.context, opts.actualDepth, opts.formatValue, opts.renderOptions);
    }

    this.canvas.endRender();
  }

  private initColorMap(data: CirclePacking) {
    const categories = (data.children || []).map((d) => d.label);
    if (this.categoryKey) {
      this.colorCategoryMap = this.themeService.registerCategorical(this.categoryKey, categories);
      return;
    }

    this.colorCategoryMap = {};
    const colors = this.themeService.getDefaultPalette(ColorPalettes.Palette_CategoricalChart);
    categories.forEach((category, i) => {
      this.colorCategoryMap[category] = colors[i % colors.length];
    });
  }

  private generateCircleData(data): CirclePacking {
    packing(data, this.colorCategoryMap, this.titleCase.transform.bind(this.titleCase));

    this.dataRange = dataRange(data);
    this.selectedChildren = data.children;

    return data;
  }

  /**
   * CLEAN OPERATIONS
   */
  private cleanCanvas() {
    if (this.canvas) {
      this.canvas.startRender({ x: this.size.width / 2, y: this.size.height / 2, k: 1 });
      this.canvas.endRender();
    }
  }

  private cleanData() {
    this.circlesData = null;
    this.dataRange = null;
    this.selectedChildren = null;
    this.activeNode = null;

    this.actualDepth = MAIN_DEPTH;
  }

  /**
   * MOUSE INTERACTIONS
   */

  /**
   * Zoom to the selected circle and send analytics if the trackEvent boolean is passed true
   */
  public zoomIn(circle: CirclePacking, trackEvent?: boolean) {
    // this data will mutate so need to capture the ancestor first
    if (!circle?.children?.length) {
      return;
    }
    this.activeNode = circle;

    this.onZoomIn.emit(circle);
    this.zoomToCircle(circle);

    if (trackEvent) {
      this.trackEvent.emit(TrackedEvents.DIVE_IN);
    }
  }

  public zoomOut() {
    this.navigationStart.next(null);
    this.onZoomOut.emit('zoomOut');

    packing(this.circlesData, this.colorCategoryMap, this.titleCase.transform.bind(this.titleCase));
    this.dataRange = dataRange(this.circlesData);

    this.selectedChildren = this.circlesData.children;

    //  We render again!
    this.zoom.transform(transition(this.svgElm), zoomTransformation(bounds(this.circlesData.children), this.size));

    this.activeNode = null;
  }

  private zoomToCircle(circle: CirclePacking) {
    this.navigationStart.next(null);

    //  Update data

    // We pack the data
    packing(circle, circle.color, this.titleCase.transform.bind(this.titleCase));

    this.dataRange = dataRange(circle);
    this.selectedChildren = circle.children;

    //  We render again!
    this.zoom.transform(transition(this.svgElm), zoomTransformation(bounds(this.selectedChildren), this.size, TITLE_OFFSET));
  }

  /**
   * zoom back to the previous ancestor of the actual circle
   * if exists
   */
  public zoomBack() {
    // going back in the breadcrumb we need to the parent
    // if does not exist we zoom out

    const parent = findParentNode(this.circlesData, this.activeNode.id);

    if (parent) {
      this.zoomIn(parent);
    } else {
      this.zoomOut();
    }

    this.trackEvent.emit(TrackedEvents.DIVE_OUT);
  }

  public selected(circle: CirclePacking) {
    if (!circle) {
      return;
    }
    const relativesSelected = getRelativesSelected(circle, this.selectModel, this.modelProperty);
    this.updateModel(circle[this.modelProperty || 'id'], relativesSelected);
  }

  public deselectAll() {
    this.updateModel(null);
  }

  private updateModel(circleId: string | Array<string>, relativesSelected?: Array<string>, skipOutput?: boolean) {
    setTimeout(() => {
      if (!circleId) {
        this.selectModel = null;
        this.selectedMap = {};
        if (!skipOutput) {
          this.selectModelChange.emit(this.selectModel);
        }
        return;
      }

      //  if is an array of circles the selection comes from outside, and we want to overwrite what ever we have
      if (Array.isArray(circleId)) {
        this.selectModel = circleId;
      } else {
        //  If is not an array, we need to add/filter the new selection
        if (!this.selectModel || this.selectModel.findIndex((d) => d === circleId) === -1) {
          this.trackEvent.emit(TrackedEvents.VALUE_SELECTED);
          let model = this.selectModel;
          //  we add the selection if is not there
          model = (model || []).concat(circleId);
          //  And we filter the relatives
          if (relativesSelected) {
            model = model.filter((d) => relativesSelected.indexOf(d) === -1);
          }

          this.selectModel = model;
        } else {
          this.trackEvent.emit(TrackedEvents.VALUE_DESELECTED);
          this.selectModel = this.selectModel.filter((d) => d !== circleId);
        }
      }

      // Update the selected map to change the styles to selected
      this.selectedMap = {};
      this.selectModel.forEach((d) => {
        this.selectedMap[d] = true;
      });

      if (!skipOutput) {
        this.selectModelChange.emit(this.selectModel);
      }
    }, 0);
  }

  public onHover(circle: CirclePacking) {
    if (this.activeNode || (circle && circle.depth !== MAIN_DEPTH) || !this.zoomTransformation) {
      this.hoveredNode = null;
      return;
    }

    this.hoveredNode = circle;
  }

  private generateUri(num: number): string {
    let uri: string = '';
    const letters = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

    for (let i = 0; i < num; i++) {
      uri += letters.charAt(Math.random() * letters.length);
    }
    return uri;
  }
}
